import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAudioRecorder } from "../../hooks/useAudioRecorder";
import {
  AttachmentType,
  ConfirmAttachmentItem,
  TicketAttachment,
  TicketMessage,
  UploadUrlFile,
  UploadUrlsResponse,
  uploadBlobToS3,
} from "../../services/supportTicket.service";

const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

interface Pending {
  id:              string;
  attachment_type: AttachmentType;
  file_name:       string;
  mime_type:       string;
  blob:            Blob;
}

function typeFromMime(mime: string): AttachmentType | null {
  if (mime.startsWith("image/")) return "photo";
  if (mime.startsWith("audio/")) return "voice_note";
  if (mime.startsWith("video/")) return "screen_recording";
  return null;
}

const LABELS: Record<AttachmentType, string> = {
  voice_note: "Voice note",
  photo: "Photo",
  screen_recording: "Screen recording",
};

/** Inline playback for an attachment inside a conversation bubble. */
export const MessageAttachments: React.FC<{ attachments: TicketAttachment[] }> = ({ attachments }) => {
  if (!attachments.length) return null;
  return (
    <div className="mt-1.5 space-y-1.5">
      {attachments.map(a => (
        <div key={a.attachment_id}>
          {!a.view_url ? (
            <p className="text-[11px] italic opacity-70">{LABELS[a.attachment_type]} · {a.file_name} (unavailable here)</p>
          ) : a.attachment_type === "voice_note" ? (
            <audio controls preload="metadata" src={a.view_url} className="h-8 w-full" />
          ) : a.attachment_type === "photo" ? (
            <a href={a.view_url} target="_blank" rel="noreferrer">
              <img src={a.view_url} alt={a.file_name} className="max-h-40 w-auto rounded-lg border border-black/10 object-cover" />
            </a>
          ) : (
            <video controls preload="metadata" src={a.view_url} className="max-h-48 w-full rounded-lg" />
          )}
        </div>
      ))}
    </div>
  );
};

interface Props {
  placeholder: string;
  requestUploadUrls: (files: UploadUrlFile[]) => Promise<UploadUrlsResponse>;
  /** Resolves with the saved message; the composer clears itself on success. */
  onSend: (body: string, attachments: ConfirmAttachmentItem[]) => Promise<TicketMessage>;
  onSent: (msg: TicketMessage) => void;
}

/** Reply box with text + photo / voice note / screen recording attachments. */
export const ReplyComposer: React.FC<Props> = ({ placeholder, requestUploadUrls, onSend, onSent }) => {
  const [text, setText] = useState("");
  const [pending, setPending] = useState<Pending[]>([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const screenRecorderRef = useRef<MediaRecorder | null>(null);
  const screenChunksRef = useRef<Blob[]>([]);
  const [isRecordingScreen, setIsRecordingScreen] = useState(false);
  const { isRecording, duration, startRecording, stopRecording } = useAudioRecorder();

  const previews = useRef<Map<string, string>>(new Map());
  const previewFor = (p: Pending) => {
    let url = previews.current.get(p.id);
    if (!url) { url = URL.createObjectURL(p.blob); previews.current.set(p.id, url); }
    return url;
  };
  useEffect(() => () => { previews.current.forEach(u => URL.revokeObjectURL(u)); }, []);

  const add = (p: Pending) => {
    if (pending.length >= MAX_ATTACHMENTS) {
      toast.error(`You can attach up to ${MAX_ATTACHMENTS} files per message.`);
      return;
    }
    if (p.blob.size > MAX_FILE_SIZE_BYTES) {
      toast.error("That file is larger than the 50 MB limit.");
      return;
    }
    setPending(prev => [...prev, p]);
  };

  const remove = (id: string) => {
    const u = previews.current.get(id);
    if (u) { URL.revokeObjectURL(u); previews.current.delete(id); }
    setPending(prev => prev.filter(p => p.id !== id));
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const t = typeFromMime(file.type);
    if (!t) { toast.error("Only photos, audio and video files can be attached."); return; }
    add({ id: crypto.randomUUID(), attachment_type: t, file_name: file.name, mime_type: file.type, blob: file });
  };

  const onVoice = async () => {
    if (isRecording) {
      const blob = await stopRecording();
      if (blob) add({ id: crypto.randomUUID(), attachment_type: "voice_note", file_name: `voice-note-${Date.now()}.webm`, mime_type: "audio/webm", blob });
      return;
    }
    try { await startRecording(); } catch { toast.error("Microphone access is required to record a voice note."); }
  };

  const onScreen = async () => {
    if (isRecordingScreen) { screenRecorderRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" : "video/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      screenRecorderRef.current = recorder;
      screenChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) screenChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(screenChunksRef.current, { type: "video/webm" });
        screenChunksRef.current = [];
        stream.getTracks().forEach(t => t.stop());
        setIsRecordingScreen(false);
        add({ id: crypto.randomUUID(), attachment_type: "screen_recording", file_name: `screen-recording-${Date.now()}.webm`, mime_type: "video/webm", blob });
      };
      stream.getVideoTracks()[0].addEventListener("ended", () => recorder.stop());
      recorder.start();
      setIsRecordingScreen(true);
    } catch {
      toast.error("Screen recording was not started (permission denied or unsupported browser).");
    }
  };

  const send = async () => {
    const body = text.trim();
    if (!body && pending.length === 0) return;
    setSending(true);
    try {
      let confirmed: ConfirmAttachmentItem[] = [];
      if (pending.length > 0) {
        const { files } = await requestUploadUrls(
          pending.map(p => ({ attachment_type: p.attachment_type, file_name: p.file_name, mime_type: p.mime_type, file_size_bytes: p.blob.size })),
        );
        await Promise.all(files.map((item, i) => (item.upload_url ? uploadBlobToS3(item.upload_url, pending[i].blob, pending[i].mime_type) : Promise.resolve())));
        confirmed = files.map((item, i) => ({
          s3_key: item.s3_key,
          attachment_type: item.attachment_type,
          file_name: pending[i].file_name,
          mime_type: pending[i].mime_type,
          file_size_bytes: pending[i].blob.size,
        }));
      }
      const msg = await onSend(body, confirmed);
      previews.current.forEach(u => URL.revokeObjectURL(u));
      previews.current.clear();
      setPending([]);
      setText("");
      onSent(msg);
    } catch (err: any) {
      toast.error(err?.message || "Could not send your reply.");
    } finally {
      setSending(false);
    }
  };

  const chip = "inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-medium";

  return (
    <div className="mt-2">
      {pending.length > 0 && (
        <ul className="mb-2 space-y-1.5">
          {pending.map(p => (
            <li key={p.id} className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700">
              <div className="flex items-center justify-between mb-1">
                <span className="truncate">{LABELS[p.attachment_type]} · {p.file_name}</span>
                <button type="button" onClick={() => remove(p.id)} className="ml-2 flex-shrink-0 text-gray-400 hover:text-red-600">Remove</button>
              </div>
              {p.attachment_type === "photo" && <img src={previewFor(p)} alt="" className="h-20 w-auto rounded border border-gray-200" />}
              {p.attachment_type === "voice_note" && <audio controls src={previewFor(p)} className="h-8 w-full max-w-xs" />}
              {p.attachment_type === "screen_recording" && <video controls src={previewFor(p)} className="max-h-32 rounded" />}
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-2">
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
          rows={2}
          maxLength={5000}
          placeholder={placeholder}
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || (!text.trim() && pending.length === 0)}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {sending ? "Sending…" : "Reply"}
        </button>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        <button type="button" onClick={() => fileRef.current?.click()} className={`${chip} border-gray-300 text-gray-700 hover:bg-gray-50`}>📎 Attach file</button>
        <input ref={fileRef} type="file" accept="image/*,audio/*,video/*" onChange={onFilePicked} className="hidden" />
        <button type="button" onClick={onVoice} className={`${chip} ${isRecording ? "border-red-300 bg-red-50 text-red-700" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>
          {isRecording ? `● ${duration}s — stop` : "🎤 Voice note"}
        </button>
        <button type="button" onClick={onScreen} className={`${chip} ${isRecordingScreen ? "border-red-300 bg-red-50 text-red-700" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>
          {isRecordingScreen ? "● Recording — stop" : "🖥 Record screen"}
        </button>
      </div>
    </div>
  );
};
